import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  ArrowLeft, Package, Truck, CheckCircle, Clock, Loader2,
  AlertTriangle, Camera, MessageSquare, ChevronRight, Shirt,
  Plus, X, Calendar
} from 'lucide-react';
import CustomerBottomNav from '@/components/CustomerBottomNav';
import CustomerSidebar from '@/components/CustomerSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending:          { label: 'Pending',    bg: 'bg-amber-50  text-amber-700  border-amber-200',  icon: Clock },
  pickup:           { label: 'Picked Up',  bg: 'bg-blue-50   text-blue-700   border-blue-200',   icon: Truck },
  processed:        { label: 'Washing',    bg: 'bg-orange-50 text-orange-700 border-orange-200', icon: Shirt },
  delivered:        { label: 'Delivered',  bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  delivery_overdue: { label: 'Overdue',    bg: 'bg-red-50    text-red-700    border-red-200',    icon: AlertTriangle },
};

const STATUS_ORDER = ['pending', 'pickup', 'processed', 'delivered'];

const CustomerOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail]       = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [issueType, setIssueType]         = useState('');
  const [issueDesc, setIssueDesc]         = useState('');
  const [submitting, setSubmitting]       = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const token = localStorage.getItem('customerToken');

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      const r = await axios.get(`${API}/customer/orders`, { headers: { Authorization: `Bearer ${token}` } });
      setOrders(r.data);
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const isOverdue = (o) => {
    if (o.status === 'delivered') return false;
    const exp = o.expected_delivery || o.expected_delivery_date;
    return exp ? new Date(exp) < new Date() : false;
  };

  const effectiveStatus = (o) => isOverdue(o) ? 'delivery_overdue' : o.status;

  const handleRaiseIssue = async () => {
    if (!issueType) { toast.error('Please select an issue type'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/customer/orders/${selectedOrder.id}/issue`,
        { issue_type: issueType, description: issueDesc },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Issue raised successfully');
      setShowIssueModal(false); setIssueType(''); setIssueDesc('');
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to raise issue'); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <CustomerSidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0 z-10">
          <button onClick={() => navigate('/customer/dashboard')}
            className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-black text-slate-900 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>My Orders</span>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8 space-y-5">

            {/* Page header */}
            <div className="hidden md:flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>My Orders</h1>
                <p className="text-sm text-slate-500 mt-0.5">{orders.length} order{orders.length !== 1 ? 's' : ''} total</p>
              </div>
              <button onClick={() => navigate('/customer/new-order')}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-xl transition-all hover:shadow-lg hover:shadow-blue-200 hover:-translate-y-0.5 text-sm">
                <Plus className="w-4 h-4" /> New Order
              </button>
            </div>

            {orders.length === 0 ? (
              <section className="bg-white border border-slate-200/80 rounded-[2rem] py-20 px-8 flex flex-col items-center text-center shadow-sm space-y-5">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center text-slate-300">
                  <Package className="w-12 h-12" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-600 mb-1">No orders yet</p>
                  <p className="text-sm text-slate-400">Place your first order and we'll take care of the rest</p>
                </div>
                <button onClick={() => navigate('/customer/new-order')} data-testid="place-first-order-btn"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-2xl shadow-md shadow-blue-200 transition-all hover:-translate-y-0.5">
                  <Plus className="w-5 h-5" /> Place Your First Order
                </button>
              </section>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => {
                  const overdue = isOverdue(order);
                  const es = effectiveStatus(order);
                  const cfg = STATUS_CONFIG[es] || STATUS_CONFIG.pending;
                  const Icon = cfg.icon;
                  const expDel = order.expected_delivery || order.expected_delivery_date;
                  const activeIdx = STATUS_ORDER.indexOf(order.status);

                  return (
                    <button key={order.id} data-testid={`customer-order-${order.id}`}
                      onClick={() => { setSelectedOrder(order); setShowDetail(true); }}
                      className={`w-full text-left bg-white border rounded-[1.5rem] p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 ${overdue ? 'border-red-200 bg-red-50/20' : 'border-slate-200/80'}`}>
                      {/* Top row */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${overdue ? 'bg-red-50' : 'bg-blue-50'}`}>
                            <Icon className={`w-5 h-5 ${overdue ? 'text-red-500' : 'text-blue-600'}`} />
                          </div>
                          <div>
                            <p className="font-bold text-slate-800">{order.total_clothes} items</p>
                            {order.clothes_type && <p className="text-xs text-slate-500 mt-0.5">{order.clothes_type}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${cfg.bg}`}>{cfg.label}</span>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>

                      {/* Dates */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-slate-50 rounded-xl px-3 py-2">
                          <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">Order Date</p>
                          <p className="text-sm font-semibold text-slate-700">{format(new Date(order.order_date), 'dd MMM yyyy')}</p>
                        </div>
                        <div className={`rounded-xl px-3 py-2 ${overdue ? 'bg-red-50' : 'bg-slate-50'}`}>
                          <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">Expected Delivery</p>
                          <p className={`text-sm font-semibold ${overdue ? 'text-red-600' : 'text-slate-700'}`}>
                            {expDel ? format(new Date(expDel), 'dd MMM yyyy') : '—'}
                          </p>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="relative">
                        <div className="absolute top-3 left-[12%] right-[12%] h-0.5 bg-slate-100" />
                        <div className="absolute top-3 left-[12%] h-0.5 bg-blue-600 transition-all"
                          style={{ width: activeIdx <= 0 ? '0%' : `${(activeIdx / (STATUS_ORDER.length - 1)) * 76}%` }} />
                        <div className="relative flex justify-between">
                          {STATUS_ORDER.map((s, i) => {
                            const done = i <= activeIdx;
                            return (
                              <div key={s} className="flex flex-col items-center gap-1 flex-1">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center z-10 text-[10px] font-bold ${done ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-400'}`}>
                                  {i + 1}
                                </div>
                                <span className={`text-[9px] font-medium ${done ? 'text-blue-600' : 'text-slate-400'}`}>
                                  {s.charAt(0).toUpperCase() + s.slice(1)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Badges */}
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {order.photos?.length > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full font-semibold">
                            <Camera className="w-3 h-3" /> {order.photos.length} photo{order.photos.length > 1 ? 's' : ''}
                          </span>
                        )}
                        {order.issue && (
                          <span className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold border ${order.issue.status === 'open' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                            <AlertTriangle className="w-3 h-3" /> Issue: {order.issue.status}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ── Order Detail Modal ── */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Package className="w-5 h-5 text-blue-600" /> Order Details
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (() => {
            const overdue = isOverdue(selectedOrder);
            const es = effectiveStatus(selectedOrder);
            const cfg = STATUS_CONFIG[es] || STATUS_CONFIG.pending;
            const StatusIcon = cfg.icon;
            const expDel = selectedOrder.expected_delivery || selectedOrder.expected_delivery_date;
            return (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border ${cfg.bg}`}>
                    <StatusIcon className="w-3.5 h-3.5" />{cfg.label}
                  </span>
                  <span className="text-sm text-slate-500 font-medium">{selectedOrder.total_clothes} items</span>
                </div>

                <div className="bg-slate-50 rounded-2xl p-4 space-y-2.5 text-sm">
                  {selectedOrder.clothes_type && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Type</span>
                      <span className="text-slate-800 font-medium">{selectedOrder.clothes_type}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-400">Order Date</span>
                    <span className="text-slate-800">{format(new Date(selectedOrder.order_date), 'dd MMM yyyy')}</span>
                  </div>
                  {selectedOrder.pickup_date && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Pickup Date</span>
                      <span className="text-slate-800">{format(new Date(selectedOrder.pickup_date), 'dd MMM yyyy')}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-400">Expected Delivery</span>
                    <span className={overdue ? 'text-red-600 font-semibold' : 'text-slate-800'}>
                      {expDel ? format(new Date(expDel), 'dd MMM yyyy') : '—'}
                    </span>
                  </div>
                  {selectedOrder.delivery_date && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Delivered On</span>
                      <span className="text-emerald-700 font-medium">{format(new Date(selectedOrder.delivery_date), 'dd MMM yyyy')}</span>
                    </div>
                  )}
                  {selectedOrder.special_instructions && (
                    <div className="pt-2 border-t border-slate-200">
                      <p className="text-slate-400 text-xs mb-1">Special Instructions</p>
                      <p className="text-slate-700">{selectedOrder.special_instructions}</p>
                    </div>
                  )}
                </div>

                {/* Photos */}
                {selectedOrder.photos?.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1.5">
                      <Camera className="w-4 h-4 text-blue-600" />
                      Verification Photos ({selectedOrder.photos.length})
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedOrder.photos.map((photo) => (
                        <button key={photo.id} data-testid={`view-photo-${photo.id}`}
                          onClick={() => { setSelectedPhoto(photo); setShowPhotoModal(true); }}
                          className="relative group rounded-xl overflow-hidden border border-slate-200">
                          <img src={`${BACKEND_URL}${photo.url}`} alt="Order verification"
                            className="w-full h-20 object-cover group-hover:opacity-80 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Issue */}
                {selectedOrder.issue ? (
                  <div className={`p-4 rounded-2xl border ${selectedOrder.issue.status === 'open' ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className={`w-4 h-4 ${selectedOrder.issue.status === 'open' ? 'text-amber-600' : 'text-emerald-600'}`} />
                      <span className={`text-sm font-bold ${selectedOrder.issue.status === 'open' ? 'text-amber-800' : 'text-emerald-800'}`}>
                        {selectedOrder.issue.issue_type}
                      </span>
                      <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${selectedOrder.issue.status === 'open' ? 'bg-amber-200 text-amber-800' : 'bg-emerald-200 text-emerald-800'}`}>
                        {selectedOrder.issue.status}
                      </span>
                    </div>
                    {selectedOrder.issue.description && <p className="text-xs text-slate-600 mt-1">{selectedOrder.issue.description}</p>}
                    {selectedOrder.issue.admin_comment && (
                      <div className="mt-2 pt-2 border-t border-slate-200">
                        <p className="text-xs text-slate-400">Admin Response:</p>
                        <p className="text-xs text-slate-700 font-medium mt-0.5">{selectedOrder.issue.admin_comment}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  selectedOrder.photos?.length > 0 && selectedOrder.status !== 'delivered' && (
                    <button data-testid="raise-issue-btn"
                      onClick={() => { setShowDetail(false); setShowIssueModal(true); }}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-amber-200 text-amber-600 hover:bg-amber-50 font-semibold text-sm transition-all">
                      <MessageSquare className="w-4 h-4" /> Raise an Issue
                    </button>
                  )
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Photo viewer */}
      <Dialog open={showPhotoModal} onOpenChange={setShowPhotoModal}>
        <DialogContent className="max-w-lg p-2 rounded-3xl">
          {selectedPhoto && (
            <img src={`${BACKEND_URL}${selectedPhoto.url}`} alt="Order verification"
              className="w-full rounded-2xl" data-testid="photo-viewer" />
          )}
        </DialogContent>
      </Dialog>

      {/* Raise issue modal */}
      <Dialog open={showIssueModal} onOpenChange={setShowIssueModal}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Raise an Issue
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Issue Type</label>
              <Select value={issueType} onValueChange={setIssueType}>
                <SelectTrigger data-testid="issue-type-select" className="rounded-xl border-slate-200 bg-slate-50">
                  <SelectValue placeholder="Select issue type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Mismatch">Clothes Mismatch</SelectItem>
                  <SelectItem value="Damaged">Clothes Damaged</SelectItem>
                  <SelectItem value="Missing Items">Missing Items</SelectItem>
                  <SelectItem value="Wrong Items">Wrong Items Received</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Description (Optional)</label>
              <Textarea value={issueDesc} onChange={e => setIssueDesc(e.target.value)}
                placeholder="Describe the issue..." className="min-h-[80px] rounded-xl border-slate-200 bg-slate-50"
                data-testid="issue-description" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setShowIssueModal(false)}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-all">
              Cancel
            </button>
            <button onClick={handleRaiseIssue} disabled={submitting || !issueType}
              data-testid="submit-issue-btn"
              className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-bold text-sm transition-all flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Issue'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomerBottomNav />
    </div>
  );
};

export default CustomerOrders;
